import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PinPad } from "@/components/common/PinPad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Mail, Eye, EyeOff, KeyRound } from "lucide-react";
import { ConnectingOverlay } from "@/components/common/ConnectingOverlay";
import SetOwnPin from "@/components/common/SetOwnPin";

const STATION_ROUTE = { kds: "/kds", pos: "/pos", manager: "/manager" };
const STATION_LABEL = { kds: "🍳 KDS Cuisine", pos: "🧾 Caisse / POS", manager: "📊 Manager" };
const ROLE_FR = { manager: "Manager", server: "Serveur", kitchen: "Cuisine", owner: "Propriétaire", admin: "Propriétaire", org_admin: "Admin groupe" };

export default function Login() {
  const { login, pinLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const station = params.get("station") || localStorage.getItem("novanext_station") || "manager";
  const slug = params.get("slug") || localStorage.getItem("novanext_last_slug") || "comptoir-parisien";
  const [mode, setMode] = useState("pin"); // pin (défaut, tous) | email (secondaire, Manager/Propriétaire)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [mustSetPin, setMustSetPin] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [selected, setSelected] = useState(null); // { id, name, role, employee_code }
  const [codeInput, setCodeInput] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(null); // "auth" pendant l'authentification, "prepare" une fois authentifié
  const [pinLen, setPinLen] = useState(4);

  useEffect(() => { api.staffTiles(slug).then(setTiles).catch(() => setTiles([])); }, [slug]);
  useEffect(() => { api.getRestaurant(slug).then((r) => setPinLen(r?.pin_length || 4)).catch(() => {}); }, [slug]);

  const routeAfter = (u) => {
    setPhase("prepare");
    localStorage.setItem("novanext_station", station);
    const role = u?.role;
    let dest = STATION_ROUTE[station] || "/manager";
    if (role === "server" && station === "manager") dest = "/pos";
    if (role === "org_admin") dest = "/group";
    navigate(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true); setPhase("auth");
    try {
      const u = await login(email, password);
      routeAfter(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setPhase(null);
    } finally {
      setLoading(false);
    }
  };

  const lookup = async (e) => {
    e?.preventDefault?.();
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setLookupBusy(true); setError("");
    try { const u = await api.pinLookup(slug, code); setSelected(u); setPin(""); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Identifiant inconnu"); }
    finally { setLookupBusy(false); }
  };

  const submitPin = async () => {
    if (!selected?.employee_code) { setError("Identifiez-vous d'abord (identifiant professionnel)"); return; }
    if (pin.length !== pinLen) { setError(`PIN à ${pinLen} chiffres`); return; }
    setError(""); setLoading(true); setPhase("auth");
    try {
      const u = await pinLogin(slug, pin, selected.employee_code);
      if (u.must_set_pin) { setMustSetPin(u); setPhase(null); setLoading(false); return; }
      routeAfter(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Identifiant ou PIN incorrect");
      setPin("");
      setPhase(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {mustSetPin && <SetOwnPin user={mustSetPin} pinLen={pinLen} onDone={() => { const u = mustSetPin; setMustSetPin(null); routeAfter(u); }} />}
    {!mustSetPin && (
    <div className="min-h-screen bg-[#0C1713] text-stone-100 grid place-items-center px-5">
      {phase && <ConnectingOverlay message={phase === "auth" ? "Connexion en cours…" : "Préparation de votre espace…"} sub={(STATION_LABEL[station] || "").replace(/^\S+\s/, "")} />}
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#C69C4A] text-[#0C1713] grid place-items-center font-serif-display font-bold text-3xl mb-4">N</div>
          <h1 className="font-serif-display text-3xl">NOVANEXT <span className="text-[#C69C4A]">TABLE</span></h1>
          <p data-testid="login-station-label" className="text-sm text-stone-400 mt-1">{STATION_LABEL[station] || "Poste restaurant"} · Connexion</p>
        </div>

        {/* PIN = parcours principal. Email + mot de passe = accès secondaire, réservé Manager/Propriétaire. */}
        <div className="flex justify-center mb-4">
          {mode === "pin" ? (
            <button type="button" data-testid="login-mode-email" onClick={() => { setMode("email"); setError(""); }}
              className="text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2">
              Manager : se connecter avec email + mot de passe
            </button>
          ) : (
            <button type="button" data-testid="login-mode-pin" onClick={() => { setMode("pin"); setError(""); }}
              className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2">
              <KeyRound className="w-3.5 h-3.5" /> Retour au PIN employé
            </button>
          )}
        </div>

        {mode === "email" ? (
        <form onSubmit={submit} className="bg-[#111F1A] border border-[#1B3028] rounded-3xl p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Email</label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@restaurant.com"
                className="pl-10 bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-12"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Mot de passe</label>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
              <Input
                data-testid="login-password"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10 bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-12"
                required
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold">
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
        ) : (
        <div className="bg-[#111F1A] border border-[#1B3028] rounded-3xl p-6">
          {!selected ? (
            <>
              <p className="text-center text-[11px] uppercase tracking-widest text-stone-500 mb-2">Votre identifiant professionnel</p>
              <form onSubmit={lookup} className="flex gap-2 mb-4">
                <Input data-testid="login-employee-code" value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder="Ex. SRV-002"
                  autoCapitalize="characters" autoComplete="off" className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl h-12 font-mono tracking-widest uppercase" />
                <Button data-testid="login-employee-code-submit" type="submit" disabled={lookupBusy || !codeInput.trim()} className="h-12 rounded-xl bg-[#1A3D32] hover:bg-[#1F4A3C] text-[#E7C97A] font-bold px-4">{lookupBusy ? "…" : "OK"}</Button>
              </form>
              {tiles.length > 0 && (
                <div>
                  <p className="text-center text-[11px] uppercase tracking-widest text-stone-500 mb-2">ou choisissez votre compte</p>
                  <div data-testid="staff-tiles" className="grid grid-cols-3 gap-2 max-h-44 overflow-y-auto">
                    {tiles.map((t) => (
                      <button key={t.id} data-testid={`staff-tile-${t.id}`} onClick={() => { setSelected(t); setError(""); setPin(""); }}
                        className="rounded-xl border border-[#1B3028] hover:border-[#C69C4A]/40 p-2 flex flex-col items-center gap-1 transition-all">
                        <span className="w-9 h-9 rounded-full bg-[#1A3D32] text-[#E7C97A] grid place-items-center font-bold text-sm">{t.initials}</span>
                        <span className="text-[11px] text-stone-200 leading-tight text-center truncate w-full">{t.name}</span>
                        <span className="text-[9px] font-mono text-[#C69C4A]">{t.employee_code || ROLE_FR[t.role] || t.role}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {error && <p data-testid="login-error" className="text-center text-sm text-red-400 mt-3">{error}</p>}
            </>
          ) : (
            <>
              <div data-testid="login-selected-employee" className="flex items-center gap-3 rounded-2xl bg-[#0A1712] border border-[#1B3028] p-3 mb-4">
                <span className="w-10 h-10 rounded-full bg-[#1A3D32] text-[#E7C97A] grid place-items-center font-bold">{selected.initials}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-stone-100 font-semibold truncate">{selected.name}</p>
                  <p className="text-[11px] text-stone-400"><span className="font-mono text-[#C69C4A]">{selected.employee_code}</span> · {ROLE_FR[selected.role] || selected.role}</p>
                </div>
                <button data-testid="login-change-employee" onClick={() => { setSelected(null); setPin(""); setError(""); setCodeInput(""); }} className="text-[11px] text-stone-400 hover:text-stone-200 underline underline-offset-2">Changer</button>
              </div>
              <p className="text-center text-xs text-stone-400 mb-4">Saisissez votre PIN personnel</p>
              <PinPad value={pin} onChange={setPin} max={pinLen} disabled={loading} dark />
              {error && <p data-testid="login-error" className="text-center text-sm text-red-400 mt-3">{error}</p>}
              <Button data-testid="login-pin-submit" onClick={submitPin} disabled={loading || pin.length !== pinLen} className="mt-4 w-full h-12 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold">
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </>
          )}
        </div>
        )}
        <p className="text-center text-xs text-stone-600 mt-6">Powered by NOVANEXT</p>
      </div>
    </div>
    )}
    </>
  );
}
